/**
 * NotificationBell – Glocke-Icon im Header mit Dropdown-Posteingang.
 * Backend: Admin-Broadcasts (Update/Neustart/Allgemein) + individuelle Scheine-Vergaben
 * (/api/v1/player-notifications, siehe notification.service.ts).
 *
 * Panel wird — analog zu HamburgerMenu.ts — einmalig an document.body gehängt und
 * überlebt so Header-Re-Renders (renderHeader() erzeugt nur den Button neu).
 */

import { api } from '../services/api.js';
import { DESKTOP_LAYOUT_QUERY } from '../utils/mobile-layout.js';

type NotificationType = 'UPDATE' | 'RESTART' | 'GENERAL' | 'SCHEINE_GRANT' | 'SCHEINE_ANNOUNCEMENT' | 'SUGGESTION_STATUS_CHANGE' | 'HIDEOUT_RAID_WARNING' | 'HIDEOUT_RAID_EXECUTED';

interface InboxNotification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  amountScheine: number | null;
  sentAt: string;
  isRead: boolean;
}

const TYPE_COLOR_VAR: Record<NotificationType, string> = {
  UPDATE: '--accent-blue',
  RESTART: '--accent-orange',
  GENERAL: '--text-primary',
  SCHEINE_GRANT: '--accent-gold',
  SCHEINE_ANNOUNCEMENT: '--accent-gold',
  SUGGESTION_STATUS_CHANGE: '--accent-purple',
  HIDEOUT_RAID_WARNING: '--accent-orange',
  HIDEOUT_RAID_EXECUTED: '--accent-red',
};

const POLL_INTERVAL_MS = 60_000;

let panelEl: HTMLElement | null = null;
let backdropEl: HTMLElement | null = null;
let isOpen = false;
let unreadCount = 0;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let currentItems: InboxNotification[] = [];

export const BELL_ICON_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>`;

/** Wird von renderHeader() in dashboard.ts eingebettet — Badge-Wert kommt aus dem gecachten Zähler. */
export function renderNotificationBellButton(): string {
  const badgeHidden = unreadCount === 0;
  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);
  return `
    <button class="dash-hamburger notif-bell-btn" id="notif-bell-btn" type="button" aria-label="Benachrichtigungen">
      ${BELL_ICON_SVG}
      <span class="notif-bell-badge" id="notif-bell-badge" ${badgeHidden ? 'hidden' : ''}>${badgeText}</span>
    </button>
  `;
}

function createStyles(): void {
  if (document.getElementById('notif-bell-styles')) return;
  const s = document.createElement('style');
  s.id = 'notif-bell-styles';
  s.textContent = `
    .notif-bell-btn { position: relative; }
    .notif-bell-badge {
      position: absolute; top: 2px; right: 2px; min-width: 15px; height: 15px;
      padding: 0 3px; border-radius: 999px; background: var(--accent-red); color: #fff;
      font-family: var(--font-mono); font-size: 9px; line-height: 15px; text-align: center;
      pointer-events: none;
    }

    .notif-bell-backdrop { position: fixed; inset: 0; z-index: 298; display: none; }
    .notif-bell-backdrop.is-open { display: block; }

    .notif-bell-panel {
      position: fixed; right: 8px; left: 8px; margin-left: auto;
      width: min(360px, calc(100vw - 16px));
      top: calc(var(--header-height-mobile-actual, var(--header-height-mobile)) + env(safe-area-inset-top) + 4px);
      max-height: 70vh; overflow-y: auto;
      background: var(--bg-surface); border: 1px solid var(--border); border-radius: var(--radius);
      z-index: 299; display: none;
    }
    .notif-bell-panel.is-open { display: block; }
    @media ${DESKTOP_LAYOUT_QUERY} { .notif-bell-panel { top: 64px; } }
    @media (min-width: 1024px) { .notif-bell-panel { top: calc(var(--header-height-desktop) + 4px); } }

    .notif-bell-header {
      padding: var(--space-3) var(--space-4); border-bottom: 1px solid var(--border);
      font-family: var(--font-mono); font-size: var(--font-size-xs); letter-spacing: .1em;
      color: var(--accent-gold);
    }
    .notif-bell-empty {
      padding: var(--space-4); font-family: var(--font-mono); font-size: var(--font-size-xs);
      color: var(--text-muted);
    }
    .notif-bell-item {
      display: flex; gap: var(--space-2); align-items: flex-start; width: 100%;
      padding: var(--space-3) var(--space-4); background: transparent; border: none;
      border-bottom: 1px solid var(--border); text-align: left; cursor: pointer;
      font-family: var(--font-mono);
    }
    .notif-bell-item:last-child { border-bottom: none; }
    .notif-bell-item:hover { background: var(--bg-elevated); }
    .notif-bell-dot { flex-shrink: 0; margin-top: 3px; font-size: 10px; line-height: 1; }
    .notif-bell-body { flex: 1; min-width: 0; }
    .notif-bell-title {
      font-size: var(--font-size-xs); letter-spacing: .06em; text-transform: uppercase;
      display: flex; justify-content: space-between; gap: var(--space-2);
    }
    .notif-bell-time { color: var(--text-muted); white-space: nowrap; font-size: var(--font-size-xs); }
    .notif-bell-text {
      font-size: var(--font-size-xs); color: var(--text-muted); margin-top: 2px;
      white-space: pre-wrap; word-break: break-word;
    }
  `;
  document.head.appendChild(s);
}

function buildPanel(): void {
  if (document.getElementById('notif-bell-panel')) return;

  backdropEl = document.createElement('div');
  backdropEl.className = 'notif-bell-backdrop';
  backdropEl.id = 'notif-bell-backdrop';
  backdropEl.addEventListener('click', closePanel);
  document.body.appendChild(backdropEl);

  panelEl = document.createElement('div');
  panelEl.className = 'notif-bell-panel';
  panelEl.id = 'notif-bell-panel';
  document.body.appendChild(panelEl);
}

function formatAgo(iso: string): string {
  const diffMs = Math.max(0, Date.now() - new Date(iso).getTime());
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${String(mins)} Min.`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `vor ${String(hours)} Std.`;
  const days = Math.floor(hours / 24);
  return `vor ${String(days)} Tag${days > 1 ? 'en' : ''}`;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function renderPanelContent(): void {
  if (!panelEl) return;

  if (currentItems.length === 0) {
    panelEl.innerHTML = `
      <div class="notif-bell-header">BENACHRICHTIGUNGEN</div>
      <div class="notif-bell-empty">KEINE BENACHRICHTIGUNGEN.</div>
    `;
    return;
  }

  const itemsHtml = currentItems.map((item) => {
    const colorVar = TYPE_COLOR_VAR[item.type];
    const dotColor = item.isRead ? 'var(--text-muted)' : `var(${colorVar})`;
    return `
      <button class="notif-bell-item" data-notif-id="${item.id}" data-read="${String(item.isRead)}">
        <span class="notif-bell-dot" style="color:${dotColor}">${item.isRead ? '○' : '●'}</span>
        <span class="notif-bell-body">
          <span class="notif-bell-title" style="color:var(${colorVar})">
            <span>${escapeHtml(item.title)}</span>
            <span class="notif-bell-time">${formatAgo(item.sentAt)}</span>
          </span>
          <span class="notif-bell-text">${escapeHtml(item.body)}</span>
        </span>
      </button>
    `;
  }).join('');

  panelEl.innerHTML = `
    <div class="notif-bell-header">BENACHRICHTIGUNGEN</div>
    ${itemsHtml}
  `;

  panelEl.querySelectorAll<HTMLButtonElement>('[data-notif-id]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.notifId;
      const alreadyRead = btn.dataset.read === 'true';
      if (id === undefined || alreadyRead) return;
      void handleMarkAsRead(id);
    });
  });
}

async function handleMarkAsRead(notificationId: string): Promise<void> {
  // Optimistisch aktualisieren — Panel bleibt offen, kein Full-Reload nötig.
  const item = currentItems.find((n) => n.id === notificationId);
  if (item && !item.isRead) {
    item.isRead = true;
    renderPanelContent();
    setUnreadCount(Math.max(0, unreadCount - 1));
  }

  try {
    await api.post(`/player-notifications/${notificationId}/read`, {});
  } catch {
    // Stiller Fehlschlag — nächster Poll-Zyklus korrigiert den Badge-Stand ohnehin.
  }
}

function setUnreadCount(count: number): void {
  unreadCount = count;
  const badge = document.getElementById('notif-bell-badge');
  if (!badge) return;
  if (count === 0) {
    badge.setAttribute('hidden', '');
  } else {
    badge.removeAttribute('hidden');
    badge.textContent = count > 99 ? '99+' : String(count);
  }
}

async function refreshUnreadCount(): Promise<void> {
  try {
    const result = await api.get<{ count: number }>('/player-notifications/unread-count');
    setUnreadCount(result.count);
  } catch {
    // Kein Auth/Netzwerkfehler-Handling nötig — nächster Poll-Zyklus versucht es erneut.
  }
}

async function loadInbox(): Promise<void> {
  if (!panelEl) return;
  panelEl.innerHTML = `
    <div class="notif-bell-header">BENACHRICHTIGUNGEN</div>
    <div class="notif-bell-empty">LADE...</div>
  `;

  try {
    currentItems = await api.get<InboxNotification[]>('/player-notifications');
    renderPanelContent();
  } catch {
    panelEl.innerHTML = `
      <div class="notif-bell-header">BENACHRICHTIGUNGEN</div>
      <div class="notif-bell-empty">FEHLER BEIM LADEN.</div>
    `;
  }
}

function openPanel(): void {
  if (!panelEl || !backdropEl) return;
  panelEl.classList.add('is-open');
  backdropEl.classList.add('is-open');
  isOpen = true;
  void loadInbox();
}

function closePanel(): void {
  if (!panelEl || !backdropEl) return;
  panelEl.classList.remove('is-open');
  backdropEl.classList.remove('is-open');
  isOpen = false;
}

export function toggleNotificationBell(): void {
  if (isOpen) { closePanel(); } else { openPanel(); }
}

/** Einmalig beim Dashboard-Mount aufrufen (analog initHamburgerMenu()). */
export function initNotificationBell(): void {
  createStyles();
  buildPanel();

  void refreshUnreadCount();
  pollTimer ??= setInterval(() => { void refreshUnreadCount(); }, POLL_INTERVAL_MS);
}
